import React from 'react';
import { Handle, Position } from 'reactflow';
import { Lock, Loader2, Play, RefreshCw, Film, ChevronDown, ChevronRight, ChevronLeft, Star, AlertTriangle } from 'lucide-react';
import type { StoryboardShot } from '../../services/studioService';
import { TYPE } from '../../constants/typeScale';

/** What a shot node needs to draw itself. Kept flat so React Flow's shallow compare can skip re-renders. */
export interface ShotNodeData {
  shot: StoryboardShot;
  /** 1-based position in the episode — follows ORDER, not identity, so it renumbers on reorder. */
  number: number;
  /** Resolved thumbnail URL (start frame, or the clip's first frame once rendered). */
  thumb?: string | null;
  selected?: boolean;
  rendering?: boolean;
  seconds?: number | null;
  /** Approved and spend-eligible. Passed IN rather than read off the shot: the storyboard has no `locked`
   *  field yet (ROADMAP W4 adds it), and inventing one here would be a lie about what is persisted. */
  locked?: boolean;
  /** Resolved URL of the generated clip, once there is one — the card plays it on hover. */
  clipUrl?: string | null;
  /** Re-make just this shot. Only offered once a clip exists; omitted while another shot is being re-made. */
  onRemake?: () => void;
  /** True while THIS shot is being re-made. */
  remaking?: boolean;
  /** True while ANY shot is being re-made — re-make is single-flight, so the others disable. */
  remakeBusy?: boolean;
  /** Edit the line in place, the way a ComfyUI widget is edited on the node itself. */
  onLineChange?: (line: string) => void;
  /** Collapse to the title bar only — ComfyUI's node collapse, for reading a long episode at a glance. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /**
   * TAKES. 0 is the live clip; 1..n are archived takes, newest first. The stepper lets a creator flip
   * between them ON the node — the selects workflow every editor already knows, without leaving the board.
   */
  takeIndex?: number;
  takeCount?: number;
  onStepTake?: (delta: number) => void;
  /** Promote the take being VIEWED to be the live clip. Only offered while viewing an archived one. */
  onUseTake?: () => void;
}

/** The execution states ComfyUI shows on a node, mapped to what a shot can be doing. */
type RunState = 'running' | 'failed' | 'ready' | 'rough' | 'locked' | 'planned';
const STATE: Record<RunState, { dot: string; ring: string; label: string }> = {
  // The running node gets the bright animated ring — ComfyUI's single most recognisable piece of feedback,
  // because it answers "what is happening right now?" without the creator hunting for a progress bar.
  running: { dot: 'bg-secondary-900 animate-pulse dark:bg-white', ring: 'border-secondary-900 shadow-[0_0_0_3px_rgba(0,0,0,.12)] dark:border-white dark:shadow-[0_0_0_3px_rgba(255,255,255,.16)]', label: 'rendering' },
  // A clip that failed is the state creators most need to SEE — an unrendered shot looks identical to a
  // never-started one otherwise, so a stuck render reads as "nothing happened" (the single most common
  // support question on tools of this kind). Amber, not red: it is retryable, not broken.
  failed: { dot: 'bg-amber-500', ring: 'border-amber-500 dark:border-amber-600/70', label: 'failed' },
  ready: { dot: 'bg-secondary-400 dark:bg-secondary-300', ring: 'border-secondary-300 dark:border-secondary-600', label: 'ready' },
  // A clip made by the cheap DRAFT pass. It has to be legible as a clip that EXISTS but is not the finished
  // one, because a 480p exploration and a final cut are otherwise indistinguishable on the board — and the
  // expensive mistake is thinking the episode is done. Dashed, so the difference reads without colour.
  rough: { dot: 'bg-secondary-400 dark:bg-secondary-300', ring: 'border-dashed border-secondary-400 dark:border-secondary-500', label: 'draft take' },
  locked: { dot: 'bg-secondary-500 dark:bg-secondary-400', ring: 'border-secondary-400 dark:border-secondary-500', label: 'locked' },
  // Nothing generated yet — the shot is written and waiting, which is not the same as holding a draft clip.
  planned: { dot: 'bg-secondary-300 dark:bg-secondary-600', ring: 'border-secondary-200 dark:border-secondary-700', label: 'planned' },
};

/**
 * ShotNode — one node on the board, built to ComfyUI's node anatomy.
 *
 * A ComfyUI node is a titled box with an input port on the left, an output port on the right, a preview area,
 * and its parameters as WIDGET ROWS edited in place. That is the shape a creator arriving from ComfyUI already
 * reads fluently, so the shot card adopts it directly: title bar (status dot, number, beat, collapse), the
 * frame as the preview, then the line as an editable widget.
 *
 * Where it deliberately DIVERGES: the ports are decorative anchors for the link curve, not wiring sockets.
 * `nodesConnectable` stays off at the board level, because a story is a sequence — you cannot run shot 1 into
 * shot 5. The ports exist so the "then" relationship is visible, which is what ComfyUI's links are actually for.
 *
 * It is a DOM node (React Flow's model) rather than something drawn on a canvas, which is what lets the
 * keyframe be a real `<img>`, the line be an editable field, and the controls be focusable buttons.
 */
const ShotNode: React.FC<{ data: ShotNodeData }> = ({ data }) => {
  const {
    shot, number, thumb, selected, rendering, seconds, locked, clipUrl,
    onRemake, remaking, remakeBusy, onLineChange, collapsed, onToggleCollapse,
    takeIndex = 0, takeCount = 1, onStepTake, onUseTake,
  } = data;
  const viewingArchived = takeIndex > 0;

  const state: RunState = rendering || remaking
    ? 'running'
    : shot.clipFailed && !shot.clipPath ? 'failed'
      : shot.clipPath ? (shot.draft ? 'rough' : 'ready') : locked ? 'locked' : 'planned';
  const s = STATE[state];

  return (
    <div
      className={`w-[172px] overflow-hidden rounded-[6px] border bg-white transition-colors dark:bg-secondary-900 ${
        selected
          ? 'border-secondary-900 shadow-[0_0_0_3px_rgba(0,0,0,.08)] dark:border-white dark:shadow-[0_0_0_3px_rgba(255,255,255,.09)]'
          : s.ring
      }`}
    >
      {/* PORTS — the anchors the link curve runs between. Rendered small and flush to the edge, as litegraph
          draws them. Not connectable: the board turns connection off, so these read as flow, not as sockets. */}
      <Handle type="target" position={Position.Left} isConnectable={false}
        className="!h-2 !w-2 !border-2 !border-white !bg-secondary-400 dark:!border-secondary-950 dark:!bg-secondary-500" />
      <Handle type="source" position={Position.Right} isConnectable={false}
        className="!h-2 !w-2 !border-2 !border-white !bg-secondary-400 dark:!border-secondary-950 dark:!bg-secondary-500" />

      {/* TITLE BAR — status dot, what this shot is, and the collapse toggle. */}
      <div className="flex items-center gap-1.5 border-b border-secondary-200 bg-secondary-50 px-2 py-1 dark:border-secondary-800 dark:bg-secondary-800/60">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${s.dot}`} aria-hidden />
        <span className="font-mono text-[11px] text-secondary-600 dark:text-secondary-300">{number}</span>
        <span className="min-w-0 flex-1 truncate text-[11px] uppercase tracking-wide text-secondary-500 dark:text-secondary-500">
          {s.label}
          {/* Pacing, surfaced in the creator's words (never the raw `weight` keys). */}
          {shot.weight === 'brief' ? ' · quick' : shot.weight === 'hold' ? ' · linger' : ''}
        </span>
        {takeCount > 1 && onStepTake ? (
          <span className="flex shrink-0 items-center gap-0.5" data-testid={`takes-${number}`}>
            <button
              type="button" aria-label={`Previous take of shot ${number}`}
              onClick={(e) => { e.stopPropagation(); onStepTake(-1); }}
              disabled={takeIndex === 0}
              className="text-secondary-500 transition-colors hover:text-secondary-900 disabled:opacity-30 dark:hover:text-white"
            >
              <ChevronLeft className="h-3 w-3" />
            </button>
            <span className="font-mono text-[11px] text-secondary-600 dark:text-secondary-300">{takeIndex + 1}/{takeCount}</span>
            <button
              type="button" aria-label={`Next take of shot ${number}`}
              onClick={(e) => { e.stopPropagation(); onStepTake(1); }}
              disabled={takeIndex >= takeCount - 1}
              className="text-secondary-500 transition-colors hover:text-secondary-900 disabled:opacity-30 dark:hover:text-white"
            >
              <ChevronRight className="h-3 w-3" />
            </button>
          </span>
        ) : (!!seconds && <span className="font-mono text-[11px] text-secondary-500">{seconds}s</span>)}
        {onToggleCollapse && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleCollapse(); }}
            aria-label={collapsed ? `Expand shot ${number}` : `Collapse shot ${number}`}
            data-testid={`collapse-${number}`}
            className="shrink-0 text-secondary-500 transition-colors hover:text-secondary-900 dark:hover:text-white"
          >
            {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        )}
      </div>

      {!collapsed && (
        <>
          {/* PREVIEW — the node's output, the way a ComfyUI preview node shows its image. */}
          <div className="group/frame relative flex aspect-[9/16] items-center justify-center bg-secondary-100 dark:bg-secondary-950">
            {clipUrl ? (
              <>
                {/* The finished clip previews in place on hover — PRODUCT.md's "hover the keyframe, the clip plays". */}
                <video
                  src={clipUrl} muted playsInline preload="metadata"
                  className="h-full w-full object-cover"
                  onMouseEnter={(e) => { void e.currentTarget.play().catch(() => {}); }}
                  onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                />
                <Play className="pointer-events-none absolute h-4 w-4 text-white/90 drop-shadow group-hover/frame:opacity-0" />
              </>
            ) : thumb ? (
              <img src={thumb} alt="" className="h-full w-full object-cover" draggable={false} />
            ) : (
              <Film className="h-5 w-5 text-secondary-400 dark:text-secondary-600" />
            )}

            {/* RE-MAKE this one shot. Single-flight: while any shot is re-making, the rest are disabled, because
                each re-make costs credits and a creator should never fire several by accident. */}
            {onRemake && shot.clipPath && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); if (!remakeBusy) onRemake(); }}
                disabled={remakeBusy}
                title={remakeBusy ? 'Another shot is being re-made' : 'Re-make this shot'}
                aria-label={`Re-make shot ${number}`}
                data-testid={`remake-${number}`}
                className="absolute right-1 top-1 inline-flex items-center justify-center rounded bg-black/55 p-1
                           text-white opacity-0 transition group-hover/frame:opacity-100 disabled:cursor-not-allowed
                           disabled:opacity-60"
              >
                {remaking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              </button>
            )}

            {/* The title bar already NAMES the state, so these are purely visual: a spinner over the frame
                being rendered, and a lock on one that is approved for spend. Repeating the word in both
                places was just noise on a 172px card. */}
            {state === 'running' && (
              <span className="absolute inset-0 flex items-center justify-center bg-black/45" aria-hidden>
                <Loader2 className="h-5 w-5 animate-spin text-white" />
              </span>
            )}
            {state === 'failed' && !viewingArchived && (
              <span className="absolute inset-x-1.5 bottom-1.5 flex items-center gap-1 rounded bg-black/75 px-1.5 py-1
                               text-[9.5px] uppercase tracking-wide text-amber-400">
                <AlertTriangle className="h-3 w-3 shrink-0" /> didn&apos;t come through — re-make
              </span>
            )}
            {state === 'locked' && !viewingArchived && (
              <Lock className="absolute bottom-1.5 left-1.5 h-3.5 w-3.5 text-white drop-shadow" aria-hidden />
            )}

            {/* Viewing an older take: say so plainly, and offer the one action that matters. Restoring is FREE
                (the clip already exists), so this never needs a spend confirmation. */}
            {viewingArchived && (
              <div className="absolute inset-x-1.5 bottom-1.5 flex items-center justify-between gap-1 rounded
                              bg-black/75 px-1.5 py-1">
                <span className={`${TYPE.micro} uppercase tracking-wide text-secondary-300`}>earlier take</span>
                {onUseTake && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onUseTake(); }}
                    data-testid={`use-take-${number}`}
                    className={`inline-flex items-center gap-1 rounded bg-white px-1.5 py-0.5 ${TYPE.micro} font-semibold
                               text-secondary-900 hover:opacity-90 dark:bg-white dark:text-secondary-900`}
                  >
                    <Star className="h-2.5 w-2.5" /> Use
                  </button>
                )}
              </div>
            )}
          </div>

          {/* WIDGET ROW — a ComfyUI node's parameters are edited ON the node, not in a far-off panel. */}
          <div className="border-t border-secondary-200 p-1.5 dark:border-secondary-800">
            {onLineChange ? (
              <textarea
                value={shot.line || ''}
                onChange={(e) => onLineChange(e.target.value)}
                onPointerDown={(e) => e.stopPropagation()}   /* type in the field, do not drag the node */
                placeholder="Say what happens in this shot…"
                aria-label={`Shot ${number} line`}
                data-testid={`line-${number}`}
                rows={3}
                className="nodrag block w-full resize-none rounded bg-secondary-50 px-2 py-1.5 text-[11px]
                           leading-snug text-secondary-800 outline-none ring-1 ring-inset ring-secondary-200
                           placeholder:text-secondary-400 focus:ring-secondary-500
                           dark:bg-secondary-950 dark:text-secondary-200 dark:ring-secondary-800
                           dark:placeholder:text-secondary-600"
              />
            ) : (
              <p className="min-h-[50px] px-1 py-0.5 text-[11.5px] leading-snug text-secondary-700 dark:text-secondary-300">
                {shot.line?.trim() || <span className="text-secondary-400 dark:text-secondary-600">Say what happens in this shot…</span>}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(ShotNode);
